import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Link2,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Cloud,
  RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mod-verktyg: "Media spiegeln"
 *
 * Master klistrar in en extern JPG/PNG/PDF-URL. Backend:
 *   1) hämtar filen (httpx, 30 s timeout, follow_redirects)
 *   2) validerar magic-bytes (JPG/PNG-header eller %PDF-)
 *   3) max 25 MB
 *   4) sparar i samma R2-bucket som vanliga uploads
 *   5) loggar i db.media_mirrors (audit-spår)
 *
 * Inget nytt infrastruktur, ingen offentlig endpoint, ingen ny DB-tabell
 * (bara en audit-collection).
 */
export default function ModMediaMirror() {
  const [token] = useState(sessionStorage.getItem("mod_token") || "");
  const [url, setUrl] = useState("");
  const [hint, setHint] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  // Build 94c.5 — Sicherheitsbestätigung
  const [confirmed, setConfirmed] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchHistory = async () => {
    try {
      const r = await axios.get(`${API}/mod/media-mirror/list`, { headers });
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch {
      /* tyst – history är extrafunktion */
    }
  };

  useEffect(() => {
    if (token) fetchHistory();
  }, [token]);

  if (!token) return <Navigate to="/mod" replace />;

  const handleMirror = async () => {
    if (!url.trim()) {
      setError("Ange en URL.");
      return;
    }
    if (!confirmed) {
      setError("Bekräfta först att källa och rättigheter är kontrollerade.");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      const r = await axios.post(
        `${API}/mod/media-mirror`,
        { url: url.trim(), filename_hint: hint.trim() || null, note: note.trim() || null },
        { headers }
      );
      setResult(r.data);
      fetchHistory();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Okänt fel");
    } finally {
      setBusy(false);
    }
  };

  const finalUrl = result
    ? `${process.env.REACT_APP_BACKEND_URL}${result.url}`
    : "";

  const copy = async () => {
    if (!finalUrl) return;
    try {
      await navigator.clipboard.writeText(finalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Link
        to="/mod"
        data-testid="link-back-mod"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-bark hover:text-pine no-underline mb-6"
      >
        <ArrowLeft size={14} /> Tillbaka till Moderation
      </Link>

      <div className="flex items-center gap-2 mb-1">
        <Cloud size={18} className="text-pine" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-bark/55">
          Master-verktyg
        </p>
      </div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-2">
        Spegla extern media till R2
      </h1>
      <p className="text-sm text-bark mb-6 leading-relaxed">
        Klistra in en extern JPG-, PNG- eller PDF-URL. Filen laddas ner,
        valideras och sparas i vårt eget R2-konto. Du får en intern{" "}
        <code className="text-xs bg-bone-alt px-1 py-0.5 rounded">
          /api/uploads/&lt;namn&gt;
        </code>
        -URL som kan användas i koden eller länkas direkt.
      </p>

      {/* Build 94c.5 — Arbeitsanweisung */}
      <div data-testid="media-warning-banner" className="mb-5 px-4 py-3 bg-amber-50 border-l-4 border-amber-500 rounded-sm text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
        <div>
          <strong>Media spegla kan påverka offentliga bilder och filer.</strong>
          <p className="mt-1 text-amber-800">Använd endast när källa och rättigheter är kontrollerade. Audit-loggen sparar URL, källa, vem och när. Befintlig R2-fil med samma filnamn kan ersättas tyst.</p>
        </div>
      </div>

      <div className="bg-parchment border border-warm-border rounded-sm p-4 sm:p-5 mb-6">
        <label className="block mb-3">
          <span className="block text-xs font-semibold text-ink mb-1">
            Extern URL <span className="text-falu">*</span>
          </span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://backe.nu/.../karta.pdf"
            data-testid="mirror-url-input"
            className="w-full text-sm bg-white border border-warm-border rounded-sm px-3 py-2 focus:border-pine outline-none"
            autoComplete="off"
          />
        </label>
        <label className="block mb-3">
          <span className="block text-xs font-semibold text-ink mb-1">
            Filnamn (valfritt)
          </span>
          <input
            type="text"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="t.ex. friluftsliv-jansjo-karta.jpg"
            data-testid="mirror-hint-input"
            className="w-full text-sm bg-white border border-warm-border rounded-sm px-3 py-2 focus:border-pine outline-none"
            autoComplete="off"
          />
          <span className="text-[10px] text-bark/55 mt-0.5 block">
            Lämnas tomt så används URL:ens sista del.
          </span>
        </label>
        <label className="block mb-4">
          <span className="block text-xs font-semibold text-ink mb-1">
            Anteckning (valfritt)
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="t.ex. 'Karta över Backe vandringseldorado'"
            data-testid="mirror-note-input"
            className="w-full text-sm bg-white border border-warm-border rounded-sm px-3 py-2 focus:border-pine outline-none"
            autoComplete="off"
          />
        </label>
        {/* Build 94c.5 — Sicherheitsbestätigung */}
        <label className="flex items-start gap-2 mb-4 cursor-pointer select-none text-xs text-bark">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="mirror-confirm-checkbox"
            className="mt-0.5"
          />
          <span>
            Jag bekräftar att <strong>källa och rättigheter</strong> är kontrollerade. Det här kan ersätta en befintlig R2-fil med samma filnamn och skapar en publik URL.
          </span>
        </label>
        <button
          onClick={handleMirror}
          disabled={busy || !url.trim() || !confirmed}
          data-testid="mirror-submit-btn"
          className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover text-parchment font-semibold text-sm px-4 py-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? (
            <>
              <RefreshCw size={14} className="animate-spin" /> Speglar…
            </>
          ) : (
            <>
              <Link2 size={14} /> Spegla nu
            </>
          )}
        </button>

        {error && (
          <div
            data-testid="mirror-error"
            className="mt-4 bg-falu/10 border border-falu/40 text-falu text-sm rounded-sm px-3 py-2 flex items-start gap-2"
          >
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div
            data-testid="mirror-result"
            className="mt-4 bg-pine/5 border border-pine/30 rounded-sm p-3"
          >
            <div className="flex items-center gap-2 mb-2 text-pine font-semibold text-sm">
              <CheckCircle2 size={16} /> Spegling klar
            </div>
            <div className="grid grid-cols-1 gap-1 text-xs text-bark mb-3">
              <div>
                <span className="font-semibold">Nyckel:</span>{" "}
                <code className="bg-white px-1 py-0.5 rounded">{result.key}</code>
              </div>
              <div>
                <span className="font-semibold">Storlek:</span>{" "}
                {(result.size_bytes / 1024).toFixed(1)} KB
              </div>
              <div>
                <span className="font-semibold">Typ:</span> {result.content_type}
              </div>
              <div>
                <span className="font-semibold">Källa:</span>{" "}
                <a
                  href={result.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pine break-all"
                >
                  {result.source_url}
                </a>
              </div>
            </div>
            <div className="bg-white border border-warm-border rounded-sm p-2 flex items-center gap-2">
              <code
                data-testid="mirror-result-url"
                className="text-[11px] flex-1 break-all text-ink"
              >
                {finalUrl}
              </code>
              <button
                onClick={copy}
                data-testid="mirror-copy-btn"
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-pine hover:text-pine-hover border border-pine/40 px-2 py-1 rounded-sm transition-colors"
                title="Kopiera URL"
              >
                {copied ? (
                  <>
                    <CheckCircle2 size={12} /> Kopierad
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Kopiera
                  </>
                )}
              </button>
            </div>
            <p className="text-[10px] text-bark/55 mt-2 italic">
              Klistra in URL:en i t.ex. <code>src/data/areas.js</code> eller
              i en moderationsrad. Filen ligger nu permanent i R2.
            </p>
          </div>
        )}
      </div>

      {/* Audit-historik */}
      <h2 className="font-heading text-base font-semibold text-ink mb-2 uppercase tracking-wider">
        Senast speglade ({history.length})
      </h2>
      {history.length === 0 ? (
        <p className="text-xs text-bark/55 italic">
          Inget speglat ännu. Klistra in en URL ovan för att börja.
        </p>
      ) : (
        <ul
          data-testid="mirror-history"
          className="divide-y divide-warm-border/60 border border-warm-border rounded-sm bg-parchment"
        >
          {history.map((h, idx) => (
            <li
              key={`${h.key}-${idx}`}
              data-testid={`mirror-history-${idx}`}
              className="px-3 py-2.5 text-xs"
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <code className="text-ink font-semibold">{h.key}</code>
                <span className="text-bark/55 shrink-0">
                  {new Date(h.mirrored_at).toLocaleString("sv-SE")}
                </span>
              </div>
              <div className="text-bark/70 break-all">
                Källa:{" "}
                <a
                  href={h.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pine"
                >
                  {h.source_url}
                </a>
              </div>
              {h.note && (
                <div className="text-bark/60 italic mt-0.5">&ldquo;{h.note}&rdquo;</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
